import type { MouseEvent, ReactNode, Ref } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { Tooltip } from './Tooltip';
import { cn } from './util';

export interface IconButtonProps extends Omit<HTMLMotionProps<'button'>, 'children' | 'ref'> {
  /** Accessible name (also shown as tooltip unless `tooltip={false}`). */
  label: string;
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'white' | 'yellow' | 'ink' | 'blue' | 'red' | 'green';
  /** Round instead of square. */
  round?: boolean;
  tooltip?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const SIZE = {
  sm: 'size-8 text-[16px] border-2 shadow-ink-xs',
  md: 'size-10 text-[20px] border-3 shadow-ink-sm',
  lg: 'size-13 text-[26px] border-3 shadow-ink',
} as const;

const TONE = {
  white: 'bg-white',
  yellow: 'bg-yellow',
  ink: 'bg-ink text-yellow',
  blue: 'bg-team-a-tint',
  red: 'bg-team-b-tint',
  green: 'bg-jury-tint',
} as const;

/** Square (or round) icon-only button with the comic press feel. */
export function IconButton({
  label,
  icon,
  size = 'md',
  tone = 'white',
  round,
  tooltip = true,
  disabled,
  className,
  onClick,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const btn = (
    <motion.button
      type={type}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={(e: MouseEvent<HTMLButtonElement>) => (disabled ? e.preventDefault() : onClick?.(e))}
      whileTap={disabled ? undefined : { scale: 0.86, y: 1 }}
      transition={{ type: 'spring', stiffness: 700, damping: 18 }}
      className={cn(
        'inline-flex shrink-0 items-center justify-center border-ink leading-none',
        'transition-[translate,box-shadow] duration-100',
        SIZE[size],
        round ? 'rounded-full' : 'rounded-[3px]',
        disabled
          ? 'bg-[#e7e1d0] opacity-60 shadow-none grayscale'
          : cn(TONE[tone], 'hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-none'),
        className,
      )}
      {...rest}
    >
      {icon}
    </motion.button>
  );
  return tooltip ? <Tooltip content={label}>{btn}</Tooltip> : btn;
}
